import { useState } from "react";
import { MenuItem } from "react-bootstrap";
import {
  ItemMenu,
  ListItem,
  ConfirmationModal,
  tenantShape,
  useNotifier,
  useApi,
  url,
  useTranslateError,
} from "lcm-iot-commons";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom-v5-compat";

type Props = {
  removeTenant: $TSFixMeFunction;
  tenant: tenantShape;
};

type AccessRights = {
  can_update: boolean;
  can_delete: boolean;
};

export default function TenantItem(props: Readonly<Props>) {
  const { tenant, removeTenant } = props;
  const api = useApi();
  const intl = useIntl();
  const navigate = useNavigate();
  const notifier = useNotifier();
  const translateError = useTranslateError();

  const [deleting, setDeleting] = useState(false);

  const { mutateAsync: deleteTenant } = api.delete.useMutation(
    `/tenants/${tenant.id}`,
    {
      onError: () => {},
    },
  );
  const { data: accessRights } = api.get.useQuery(
    "/users/current/access_rights",
    {
      permitable_type: "Tenant",
      permitable_id: tenant.id,
    },
    { enabled: !!tenant.id },
  );

  const handleOnEditClick = () => navigate(url(`/tenants/${tenant.id}/edit`));

  const handleOnDeleteClick = () => setDeleting(true);

  const handleOnModalClose = () => setDeleting(false);

  const handleOnConfirm = async () => {
    try {
      setDeleting(false);
      await deleteTenant();

      notifier.showSuccess(
        intl.formatMessage({
          id: "tenants.actions.delete.notification",
        }),
      );
      removeTenant();
    } catch (error) {
      notifier.showError(translateError(error));
    }
  };

  const modalDeleting = deleting ? (
    <ConfirmationModal
      id="tenant-item-confirm"
      data-testid="tenant-item-confirm"
      show
      titleText={intl.formatMessage({
        id: "tenants.actions.delete.modal_title",
      })}
      messageText={intl.formatMessage({
        id: "tenants.actions.delete.modal_message",
      })}
      onConfirm={handleOnConfirm}
      onClose={handleOnModalClose}
    />
  ) : null;

  const actions = (
    <div id="tenant-action-items">
      <ItemMenu>
        <MenuItem
          id="item-edit"
          onSelect={handleOnEditClick}
          disabled={!(accessRights as AccessRights)?.can_update}
        >
          {intl.formatMessage({ id: "button.edit" })}
        </MenuItem>
        <MenuItem
          id="item-delete"
          onSelect={handleOnDeleteClick}
          disabled={!(accessRights as AccessRights)?.can_delete}
        >
          {intl.formatMessage({ id: "button.delete" })}
        </MenuItem>
      </ItemMenu>
      {modalDeleting}
    </div>
  );

  return (
    <ListItem
      id={`tenant-item-${tenant.id}`}
      icon="lcm-iot-icon-tenant"
      actions={actions}
      title={tenant.name}
      target={url(`/tenants/${tenant.id}`)}
      description={tenant.description}
    />
  );
}
